import { useQuery } from "@tanstack/react-query";

import type {
  InmailTemplateSchemaType,
  MessageTemplateSchemaType,
  NoteTemplateSchemaType,
} from "features/templates/schemas";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { post } from "common/helpers/HTTP";

interface PreviewStep {
  method: "injection" | "translate";
  message: string;
  // injection step doesn't have a subject
  subject?: string;
}

type TemplatePreviewData = {
  senderProfileId: string;
  targetProfileId: string;
  template:
    | Pick<
        InmailTemplateSchemaType,
        "message" | "ai_descriptors" | "subject" | "type"
      >
    | Pick<
        NoteTemplateSchemaType | MessageTemplateSchemaType,
        "message" | "ai_descriptors" | "type"
      >;
  campaignData?: {
    campaignId: string;
    isTranslateEnabled: boolean;
    sourceLanguageCode: string;
  };
};

async function templatePreviewRequest(
  workspaceId: string,
  previewData: TemplatePreviewData,
): Promise<PreviewStep[]> {
  const { senderProfileId, targetProfileId, campaignData, template } =
    previewData;
  const { type, message, ai_descriptors } = template;

  let payload: object = {
    account_id: senderProfileId,
    previewed_lead_id: targetProfileId,
    template_text: message,
    ai_descriptors,
    type,
    subject: template.type === "INMAIL_TEMPLATE" ? template.subject : undefined,
  };

  if (campaignData) {
    const { campaignId, isTranslateEnabled, sourceLanguageCode } = campaignData;
    payload = {
      ...payload,
      translate_flag: isTranslateEnabled,
      source_language: sourceLanguageCode || "en",
      campaign_id: campaignId,
    };
  }

  const { steps } = await post(
    `workspaces/${workspaceId}/templates-preview?detailed=true`,
    payload,
  );

  return steps;
}

export default function useTemplatePreview({
  previewData,
}: {
  previewData: TemplatePreviewData;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const queryKey: (string | object)[] = ["preview", workspaceId, previewData];

  const {
    data,
    error: previewError,
    refetch: refetchPreview,
    isFetching: isLoadingPreview,
  } = useQuery({
    queryKey,
    queryFn: () => templatePreviewRequest(workspaceId, previewData),
    enabled:
      !!previewData.template &&
      !!previewData.targetProfileId &&
      !!previewData.senderProfileId,
  });

  let injectContent: PreviewStep;
  let translateContent: PreviewStep;

  if (data) {
    injectContent = data.find((step) => step.method === "injection");
    translateContent = data.find((step) => step.method === "translate");
  }

  return {
    injectContent,
    translateContent,
    isLoadingPreview,
    refetchPreview,
    previewError,
  };
}
